import type { CSSProperties } from 'react';
import { WL_ICONS, type WorklynxIconName } from './iconData';

/**
 * The WorkLynx icon set, in two variants that share one name each.
 *
 * `inline` is a 24x24 stroke glyph in currentColor: it takes the colour of the text it
 * sits beside, which is what makes it legible at 16-20px and in either theme. Everything
 * in the running product - sidebar, buttons, table rows, status chips - uses this one.
 *
 * `tile` is the original 48x48 cut-corner tile, for places an icon is meant to be looked
 * at rather than scanned past: empty states, plan cards, onboarding, the home page.
 *
 * The first version of this set had only the tile. At 20px in a dark sidebar the navy
 * tile vanished into the background and the glyph inside collapsed, which is the whole
 * reason the inline variant exists.
 */

/** The tile outline: a square with two opposite corners cut. */
const TILE_PATH = 'M11 0H48V37L37 48H0V11Z';

/** Tile glyphs need a heavier stroke as they shrink, or the detail fills in. */
function tileStroke(size: number): number {
  return size <= 20 ? 3 : size <= 28 ? 2.7 : size <= 40 ? 2.4 : 2.1;
}

export interface WorklynxIconProps {
  name: WorklynxIconName;
  size?: number;
  /** 'inline' for anything beside text; 'tile' only at 32px and above. */
  variant?: 'inline' | 'tile';
  /** Turn the blue accent off on a coloured button, where it clashes. */
  accent?: boolean;
  /** A title makes the icon an image with a name; omit it beside a visible label. */
  title?: string;
  className?: string;
  style?: CSSProperties;
  strokeWidth?: number;
}

export function WorklynxIcon({
  name,
  size = 20,
  variant = 'inline',
  accent = true,
  title,
  className,
  style,
  strokeWidth,
}: WorklynxIconProps) {
  const entry = WL_ICONS[name];
  if (!entry) {
    if (import.meta.env.DEV) console.warn(`WorklynxIcon: unknown icon "${name}"`);
    return null;
  }

  const a11y = title ? { role: 'img' as const, 'aria-label': title } : { 'aria-hidden': true };

  if (variant === 'tile') {
    const [main, acc] = entry.t;
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" className={className} style={style} {...a11y}>
        {title && <title>{title}</title>}
        <path d={TILE_PATH} fill="var(--wl-tile, #08111F)" />
        <g
          transform="translate(10.4 10.4) scale(1.13)"
          fill="none"
          strokeWidth={strokeWidth ?? tileStroke(size)}
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          {main.map((d, i) => (
            <path key={`m${i}`} d={d} stroke="var(--wl-glyph, #FFFFFF)" />
          ))}
          {acc.map((d, i) => (
            <path key={`a${i}`} d={d} stroke={accent ? 'var(--wl-accent, #2F6BFF)' : 'var(--wl-glyph, #FFFFFF)'} />
          ))}
        </g>
      </svg>
    );
  }

  const [main, acc] = entry.i;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? 1.5}
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={className}
      style={style}
      {...a11y}
    >
      {title && <title>{title}</title>}
      {main.map((d, i) => (
        <path key={`m${i}`} d={d} />
      ))}
      {acc.map((d, i) => (
        <path key={`a${i}`} d={d} stroke={accent ? 'var(--wl-accent, #2F6BFF)' : 'currentColor'} />
      ))}
    </svg>
  );
}
